import React, { useState, useRef, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';
import './AudioPlayer.css';

const AudioPlayer = ({ citation, onAudioGenerated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  // Generate audio when component mounts
  useEffect(() => {
    if (citation && citation.sanskrit) {
      generateAudio();
    }
  }, [citation]);

  const generateAudio = async () => {
    if (!citation || !citation.sanskrit) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.AUDIO_GENERATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          citations: [citation]
        }),
      });

      const data = await response.json();

      if (data.success && data.results && data.results.length > 0) {
        const result = data.results[0];
        if (result.success) {
          // Use the proper API endpoint for audio file
          const audioFileUrl = API_ENDPOINTS.AUDIO_FILE(result.citationId);
          setAudioUrl(audioFileUrl);
          if (onAudioGenerated) {
            onAudioGenerated({ ...result, audioUrl: audioFileUrl });
          }
        } else {
          setError(result.error || 'Failed to generate audio');
        }
      } else {
        setError('Failed to generate audio');
      }
    } catch (err) {
      console.error('Audio generation error:', err);
      setError('Failed to generate audio');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleAudioError = () => {
    setError('Failed to play audio');
    setIsPlaying(false);
  };

  if (error) {
    return (
      <div className="shlok-audio-player error">
        <button 
          className="shlok-audio-btn error-btn"
          onClick={generateAudio}
          title="Retry audio generation"
        >
          <div className="shlok-audio-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="shlok-audio-player loading">
        <div className="shlok-audio-btn loading-btn">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!audioUrl) {
    return (
      <div className="shlok-audio-player">
        <button 
          className="shlok-audio-btn generate-btn"
          onClick={generateAudio}
          title="Generate audio for Sanskrit text"
        >
          <div className="shlok-audio-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="shlok-audio-player">
      <button 
        className={`shlok-audio-btn ${isPlaying ? 'playing' : 'play'}`}
        onClick={togglePlayPause}
        title={isPlaying ? 'Pause audio' : 'Play Sanskrit audio'}
      >
        <div className="shlok-audio-icon">
          {isPlaying ? (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </div>
      </button>
      <audio
        ref={audioRef}
        src={audioUrl}
        onEnded={handleAudioEnded}
        onError={handleAudioError}
        preload="none"
      />
    </div>
  );
};

export default AudioPlayer;
